<template>
  <div class="model-viewer">
    <div class="toolbar">
      <input type="file" accept=".glb,.gltf,.fbx" @change="onFileChange">
      <select v-model="presetAngle">
        <option value="front">正视图</option>
        <option value="left">左视图</option>
        <option value="top">俯视图</option>
        <option value="iso">等距视图</option>
      </select>
      <button @click="applyPreset">应用角度</button>
      <button @click="downloadSnapshot">下载当前视角图片</button>
    </div>
    <div class="canvas-container">
      <canvas ref="canvas"></canvas>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js'

export default {
  name: 'ModelViewerView',
  data() {
    return {
      scene: null,
      camera: null,
      renderer: null,
      controls: null,
      model: null,
      presetAngle: 'iso',
      modelCenter: new THREE.Vector3(),
      modelRadius: 1,
      animId: null
    }
  },
  mounted() {
    this.initThree()
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    cancelAnimationFrame(this.animId)
    if (this.renderer) this.renderer.dispose()
    if (this.controls) this.controls.dispose()
  },
  methods: {
    initThree() {
      const canvas = this.$refs.canvas
      const width = canvas.parentElement.clientWidth
      const height = Math.max(500, window.innerHeight - 160)
      this.scene = new THREE.Scene()
      this.scene.background = new THREE.Color(0xf4f6f8)
      this.camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 10000)
      this.camera.position.set(3, 2, 3)
      this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, preserveDrawingBuffer: true })
      this.renderer.setPixelRatio(window.devicePixelRatio)
      this.renderer.setSize(width, height)
      const hemiLight = new THREE.HemisphereLight(0xffffff, 0x444444, 1)
      hemiLight.position.set(0, 20, 0)
      this.scene.add(hemiLight)
      const dirLight = new THREE.DirectionalLight(0xffffff, 0.8)
      dirLight.position.set(5, 10, 7.5)
      dirLight.castShadow = true
      this.scene.add(dirLight)
      const grid = new THREE.GridHelper(200, 50, 0xcccccc, 0xeeeeee)
      grid.position.y = 0
      this.scene.add(grid)
      this.controls = new OrbitControls(this.camera, this.renderer.domElement)
      this.controls.enableDamping = true
      this.controls.dampingFactor = 0.05
      this.controls.target.copy(this.modelCenter)
      const animate = () => {
        this.controls.update()
        this.renderer.render(this.scene, this.camera)
        this.animId = requestAnimationFrame(animate)
      }
      animate()
    },
    onResize() {
      if (!this.renderer || !this.camera) return
      const canvas = this.$refs.canvas
      const width = canvas.parentElement.clientWidth
      const height = Math.max(500, window.innerHeight - 160)
      this.camera.aspect = width / height
      this.camera.updateProjectionMatrix()
      this.renderer.setSize(width, height)
    },
    onFileChange(e) {
      const file = e.target.files[0]
      if (!file) return
      const name = file.name.toLowerCase()
      const url = URL.createObjectURL(file)
      if (name.endsWith('.glb') || name.endsWith('.gltf')) {
        this.loadGLTF(url)
      } else if (name.endsWith('.fbx')) {
        this.loadFBX(url)
      } else {
        this.$message && this.$message.error('仅支持glb/gltf/fbx')
      }
    },
    clearModel() {
      if (this.model) {
        this.scene.remove(this.model)
      }
      this.model = null
    },
    loadGLTF(url) {
      this.clearModel()
      const loader = new GLTFLoader()
      loader.load(url, gltf => {
        const obj = gltf.scene || gltf.scenes[0]
        this.attachModel(obj)
      }, undefined, err => {
        this.$message && this.$message.error('模型加载失败')
        console.error(err)
      })
    },
    loadFBX(url) {
      this.clearModel()
      const loader = new FBXLoader()
      loader.load(url, obj => {
        this.attachModel(obj)
      }, undefined, err => {
        this.$message && this.$message.error('模型加载失败')
        console.error(err)
      })
    },
    attachModel(obj) {
      this.model = obj
      this.scene.add(this.model)
      this.updateBounds()
      this.fitCamera()
      this.controls.target.copy(this.modelCenter)
      this.applyPreset()
    },
    updateBounds() {
      const box = new THREE.Box3().setFromObject(this.model)
      box.getCenter(this.modelCenter)
      const size = new THREE.Vector3()
      box.getSize(size)
      this.modelRadius = Math.max(size.x, size.y, size.z) * 0.5
    },
    fitCamera() {
      const fov = this.camera.fov * (Math.PI / 180)
      const distance = this.modelRadius / Math.sin(fov / 2)
      const dir = new THREE.Vector3(1, 1, 1).normalize()
      const pos = new THREE.Vector3().copy(this.modelCenter).add(dir.multiplyScalar(distance))
      this.camera.position.copy(pos)
      this.camera.near = Math.max(0.01, distance / 1000)
      this.camera.far = distance * 100
      this.camera.updateProjectionMatrix()
    },
    applyPreset() {
      if (!this.model) return
      const distance = Math.max(3, this.modelRadius * 3)
      let pos = new THREE.Vector3()
      if (this.presetAngle === 'front') {
        pos.set(0, 0, distance)
      } else if (this.presetAngle === 'left') {
        pos.set(-distance, 0, 0)
      } else if (this.presetAngle === 'top') {
        pos.set(0, distance, 0)
      } else {
        pos.set(distance, distance, distance).normalize().multiplyScalar(distance)
      }
      this.camera.position.copy(new THREE.Vector3().copy(this.modelCenter).add(pos))
      this.controls.target.copy(this.modelCenter)
      this.controls.update()
    },
    downloadSnapshot() {
      const dataURL = this.renderer.domElement.toDataURL('image/png')
      const a = document.createElement('a')
      a.href = dataURL
      a.download = 'snapshot.png'
      a.click()
    }
  }
}
</script>

<style scoped>
.model-viewer {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.toolbar {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 12px;
  background: #fff;
  border-bottom: 1px solid #eaeaea;
}
.canvas-container {
  flex: 1;
  width: 100%;
  height: calc(100vh - 120px);
}
.canvas-container canvas {
  width: 100%;
  height: 100%;
  display: block;
}
</style>
